'use es6';

import { serializeStyles } from 'rich-text-lib/utils/dom';
import { getPluginSetting } from '../utils';
import { MEDIA_SETTINGS, DefaultIframeDimensions, IFRAME_MIN_WIDTH, HS_EMBED_WRAPPER_CLASS } from '../constants/hsMedia';
import { getReplacePlaceholderWithHubL } from './transforms';
const PixelRegex = /^[0-9]+$/;
export const calcAspectRatio = ({
  width,
  height
} = {}) => {
  if (!PixelRegex.test(width) || !PixelRegex.test(height)) {
    width = DefaultIframeDimensions.WIDTH;
    height = DefaultIframeDimensions.HEIGHT;
  }
  const ratio = height / width;
  return `${(ratio * 100).toFixed(2)}%`;
};
export const getValidPixelDimensions = (rawWidth, rawHeight) => {
  let width = Math.round(rawWidth);
  let height = Math.round(rawHeight);
  width = PixelRegex.test(width) ? width : `${DefaultIframeDimensions.WIDTH}`;
  height = PixelRegex.test(height) ? height : `${DefaultIframeDimensions.HEIGHT}`;
  return {
    width,
    height
  };
};
export const getResponsiveEmbedFullWidthStyles = (asAttr = false) => ({
  display: 'block',
  float: null,
  [asAttr ? 'maxWidth' : 'max-width']: null,
  [asAttr ? 'maxHeight' : 'max-height']: null,
  [asAttr ? 'marginLeft' : 'margin-left']: 'auto',
  [asAttr ? 'marginRight' : 'margin-right']: 'auto'
});
export const ResponsiveEmbedIframeStyles = {
  position: 'absolute',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  border: 'none'
};
export const getResponsiveEmbedInnerWrapperStyles = ({
  width,
  height,
  asAttr = false
}) => ({
  position: 'relative',
  overflow: 'hidden',
  [asAttr ? 'maxWidth' : 'max-width']: '100%',
  [asAttr ? 'paddingBottom' : 'padding-bottom']: calcAspectRatio({
    width,
    height
  }),
  margin: '0'
});
export const getResponsiveEmbedWrapperStyles = ({
  width,
  height,
  isFullWidth,
  centered = false,
  asAttr = false
}) => {
  const styles = Object.assign({
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    height: 'auto',
    padding: '0'
  }, width ? {
    [asAttr ? 'maxWidth' : 'max-width']: `${width}px`
  } : {}, {
    [asAttr ? 'maxHeight' : 'max-height']: height ? `${height}px` : null,
    [asAttr ? 'minWidth' : 'min-width']: `${IFRAME_MIN_WIDTH}px`
  }, centered ? {
    display: 'block',
    margin: 'auto'
  } : {}, isFullWidth ? getResponsiveEmbedFullWidthStyles(asAttr) : {});
  return styles;
};
export const getEmbedlyOptions = content => {
  const embedOptions = [];
  if (content.match(/data-card-controls/i)) {
    embedOptions.push('no-social');
  }
  if (content.match(/data-card-theme="dark"/i)) {
    embedOptions.push('darktheme');
  }
  if (content.match(/data-card-type="article"/i)) {
    embedOptions.push('small');
  }
  if (content.match(/data-card-width/i)) {
    const ref = new RegExp(/data-card-width="([0-9]+)"/).exec(content);
    if (ref !== null) {
      const width = ref[1];
      embedOptions.push(`width=${width}`);
    }
  }
  if (content.match(/data-card-align/)) {
    const ref = new RegExp(/data-card-align="([a-zA-Z]+)"/).exec(content);
    if (ref !== null) {
      const align = ref[1];
      embedOptions.push(`align=${align}`);
    }
  }
  if (content.match(/<blockquote/i)) {
    embedOptions.push('seo');
  }
  return embedOptions.join();
};
const getEmbedlyImageTagOptions = scriptOptions => {
  let embedlyTag = '';
  if (scriptOptions !== null) {
    if (scriptOptions.indexOf('no-social') > -1) embedlyTag += 'data-card-controls="0" ';
    if (scriptOptions.indexOf('darktheme') > -1) embedlyTag += 'data-card-theme="dark" ';
    if (scriptOptions.indexOf('small') > -1) embedlyTag += 'data-card-type="article" ';
    if (scriptOptions.indexOf('width') > -1) {
      const ref = new RegExp(/width=([0-9]+)/).exec(scriptOptions);
      if (ref !== null) {
        const width = ref[1];
        embedlyTag += `data-card-width="${width}" `;
      }
    }
    if (scriptOptions.indexOf('align') > -1) {
      const ref = new RegExp(/align=([a-zA-Z]+)/).exec(scriptOptions);
      if (ref !== null) {
        const align = ref[1];
        embedlyTag += `data-card-align="${align}" `;
      }
    }
  }
  return embedlyTag;
};
const imageTagToScript = tag => {
  const scriptType = tag.attr('data-embed-type');
  const scriptSrc = tag.attr('data-embed-src');
  const scriptTitle = tag.attr('data-embed-title');
  const scriptOptions = tag.attr('data-embed-options');
  const scriptDescription = tag.attr('data-embed-description');
  if (scriptType === 'wistia') {
    const popover = scriptOptions !== null ? scriptOptions.indexOf('popover') > -1 : null;
    const link = scriptOptions !== null ? scriptOptions.indexOf('link') > -1 : null;
    const responsive = scriptOptions !== null ? scriptOptions.indexOf('responsive') > -1 : null;
    let width;
    let height;
    if (scriptOptions !== null) {
      width = scriptOptions.match(/width=(\d*)/) ? scriptOptions.match(/width=(\d*)/)[1] : null;
      height = scriptOptions.match(/height=(\d*)/) ? scriptOptions.match(/height=(\d*)/)[1] : null;
    }
    const scripts = `<script src="https://fast.wistia.com/embed/medias/${scriptSrc}.jsonp" async></script><script src="https://fast.wistia.com/assets/external/E-v1.js" async></script>`;
    let wistiaContent = null;
    if (popover) {
      if (link) {
        wistiaContent = `<span class="wistia_embed wistia_async_${scriptSrc} popover=true popoverContent=link" style="display:inline"><a href="#">${scriptTitle}</a></span>`;
      } else if (responsive) {
        wistiaContent = `<div class="wistia_responsive_padding" style="padding:56.25% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><span class="wistia_embed wistia_async_${scriptSrc} popover=true popoverAnimateThumbnail=true videoFoam=true" style="display:inline-block;height:100%;width:100%">&nbsp;</span></div></div>`;
      } else {
        wistiaContent = `<span class="wistia_embed wistia_async_${scriptSrc} popover=true popoverAnimateThumbnail=true" style="display:inline-block;height:${height}px;width:${width}px">&nbsp;</span>`;
      }
    } else if (responsive) {
      wistiaContent = `<div class="wistia_responsive_padding" style="padding:56.25% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><div class="wistia_embed wistia_async_${scriptSrc} videoFoam=true" style="height:100%;width:100%">&nbsp;</div></div></div>`;
    } else {
      wistiaContent = `<div class="wistia_embed wistia_async_${scriptSrc}" style="height:${height}px;width:${width}px">&nbsp;</div>
      `;
    }
    return `${scripts}\n${wistiaContent}`;
  } else if (scriptType === 'embedly') {
    const tagOptions = getEmbedlyImageTagOptions(scriptOptions);
    if (scriptOptions !== null && scriptOptions.indexOf('seo') > -1) {
      const embedlyTag = `<blockquote class="embedly-card" ${tagOptions}><h4><a href="${scriptSrc}">${scriptTitle}</a></h4><p>${scriptDescription}</p></blockquote><script async src="//cdn.embedly.com/widgets/platform.js" charset="UTF-8"></script>`;
      return embedlyTag;
    }
    // otherwise
    const embedlyTag = `<a class="embedly-card" ${tagOptions} href="${scriptSrc}">${scriptTitle}</a><script async="" src="//cdn.embedly.com/widgets/platform.js" charset="UTF-8"></script>`;
    return embedlyTag;
  }
  return null;
};
export const setEmbedMenuContent = target => {
  return imageTagToScript(target).replace(/\\\\/g, '\\').replace(/\\'/g, "'").replace(/\\"/g, '"').replace(/\n/g, '');
};
export function getSource(editor, target) {
  if (editor.dom.hasClass(target, HS_EMBED_WRAPPER_CLASS)) {
    return target.dataset.mceEmbedContent;
  }
  if (target.getAttribute('data-mce-object')) {
    return editor.selection.getContent();
  }
  if (editor.dom.hasClass(target, 'hs-embeddable')) {
    const hublNode = getReplacePlaceholderWithHubL(editor)(target);
    return hublNode.textContent;
  }
  return '';
}
export function getEmbedPluginSetting(editor, setting) {
  const embedPluginSettings = getPluginSetting(editor, MEDIA_SETTINGS);
  if (embedPluginSettings) {
    return embedPluginSettings[setting];
  }
  return null;
}

// these are regex patterns for determining service types via embed codes, not URL's.
// use `urlPatterns` to define URL formats, which automatically create a service identifier
const embedServiceTypes = [{
  type: 'youtube',
  pattern: /https:\/\/(?:www\.)?youtube\.com\/embed/
}, {
  type: 'twitter',
  pattern: /https:\/\/(?:www\.)?twitter\.com\/.+\//
}, {
  type: 'facebook',
  pattern: /https:\/\/(?:www\.)?facebook\.com\/.+\//
}, {
  type: 'instagram',
  pattern: /https:\/\/(?:www\.)?instagram\.com\/.+\//
}, {
  type: 'pinterest',
  pattern: /https:\/\/(?:www\.)?pinterest\.com\/.+\//
}, {
  type: 'google-maps',
  pattern: /https:\/\/(?:www\.)?google\.com\/maps\/embed/
}, {
  type: 'google-calendar',
  pattern: /https:\/\/(?:www\.)?calendar\.google\.com\/calendar\/embed/
}, {
  type: 'google-docs',
  pattern: /https:\/\/(?:www\.)?docs\.google\.com\/.+\/document/
}, {
  type: 'google-sheets',
  pattern: /https:\/\/(?:www\.)?docs\.google\.com\/.+\/spreadsheets/
}, {
  type: 'google-slides',
  pattern: /https:\/\/(?:www\.)?docs\.google\.com\/.+\/presentation/
}, {
  type: 'google-drive-folder',
  pattern: /https:\/\/(?:www\.)?drive\.google\.com\/embeddedfolder/
}, {
  type: 'slideshare',
  pattern: /https:\/\/(?:www\.)?slideshare\.net\/slideshow\/embed_code/
}, {
  type: 'twentythree',
  pattern: /https:\/\/(?:www\.)?(\S+)\/((?:\d+|v)\.ihtml\/player\.html)/
}, {
  type: 'giphy',
  pattern: /https:\/\/(?:www\.)?giphy\.com\/embed/
}, {
  type: 'wistia',
  pattern: /(?:https:)?\/\/(?:www\.)?fast\.wistia\.net\/embed\/iframe/
}, {
  type: 'vimeo',
  pattern: /https:\/\/(?:www\.)?player\.vimeo\.com\/video/
}];
const genericEmbedDomainPattern = [/<iframe\s+.*src="(?:http:|https:)?\/\/(?:www\.)?([a-zA-Z,0-9]+(?:.[a-zA-Z,0-9]+)?)\..*".*>/, /<script\s+.*src="(?:http:|https:)?\/\/(?:www\.)?([a-zA-Z,0-9]+(?:.[a-zA-Z,0-9]+)?)\..*".*>/];
export const identifyEmbedService = embedData => {
  if (embedData.service) return embedData.service;
  const serviceMatch = embedServiceTypes.find(serviceInstance => {
    return serviceInstance.pattern.test(embedData.embed);
  });
  if (serviceMatch && serviceMatch.type) return serviceMatch.type;
  const domainMatch = genericEmbedDomainPattern.find(pattern => {
    return pattern.test(embedData.embed);
  });
  if (domainMatch) return domainMatch.exec(embedData.embed)[1];
  return undefined;
};
const dimensionPatterns = {
  width: /<iframe\s+.*width="(.*?)"/,
  height: /<iframe\s+.*height="(.*?)"/
};
export const checkForEmbedDimensions = embedData => {
  if (embedData.width && embedData.height) {
    return true;
  }
  let hasDimensions = true;
  Object.keys(dimensionPatterns).forEach(dim => {
    const match = dimensionPatterns[dim].exec(embedData.embed);
    hasDimensions = hasDimensions && match && !!match[1];
  });
  return !!hasDimensions;
};
const parser = new DOMParser();

// Wrap the iframe or video node in a responsive <div> and
// adjust the iframe dimensions if they are too small
export const generateResponsiveEmbedVideo = (html = '', dimensions = {}) => {
  const {
    body
  } = parser.parseFromString(html, 'text/html');
  const targetEls = body.querySelectorAll('iframe,video');
  let responsive = false;
  if (targetEls.length === 1) {
    const node = targetEls[0];
    if (node.parentElement === body) {
      let {
        width,
        height
      } = dimensions;
      if (!width || !height) {
        width = node.getAttribute('width');
        height = node.getAttribute('height');
      }
      if (width < IFRAME_MIN_WIDTH) {
        // Update the dimensions to honor the IFRAME_MIN_WIDTH constraint
        const ratio = height / width;
        width = IFRAME_MIN_WIDTH;
        height = IFRAME_MIN_WIDTH * ratio;
        node.setAttribute('width', width);
        node.setAttribute('height', height);
      }
      dimensions.width = width;
      dimensions.height = height;
      const responsiveWrapper = document.createElement('div');
      responsiveWrapper.style = serializeStyles(getResponsiveEmbedInnerWrapperStyles(Object.assign({}, dimensions)));
      node.style = serializeStyles(ResponsiveEmbedIframeStyles);
      responsiveWrapper.appendChild(node.cloneNode(true));
      body.replaceChild(responsiveWrapper, node);
      html = body.innerHTML;
      responsive = true;
    } else {
      // Check if the embed is already responsive
      responsive = node.parentElement.style.paddingBottom !== '';
    }
  }
  return {
    html,
    responsive,
    dimensions,
    provider: identifyEmbedService({
      embed: html
    })
  };
};
export const updateRichEmbed = (html, provider) => {
  const tempDoc = parser.parseFromString(html, 'text/html');
  let updated = false;

  /**
   * TODO: We should be able to use oembed params for these in the insert modal
   */
  if (provider === 'twitter') {
    // Automatically center all twitter embeds
    const blockquote = tempDoc.querySelector('blockquote');
    blockquote.setAttribute('align', 'center');
    updated = true;
  } else if (provider === 'instagram') {
    // We can hide caption if we want to
    // const blockquote = tempDoc.querySelector('blockquote');
    // blockquote.removeAttribute('data-instgrm-captioned');
    // updated = true;
  }
  return updated ? tempDoc.body.innerHTML : html;
};

// Creates an image tag of 100% width
export const generateResponsiveEmbedImage = url => {
  return `<img src="${url}" style="width: 100%;" />`;
};
const protocolRegex = /^(https?:)?(?:\/\/)(\S*)$/;
const protocolIdx = 1;
const urlIdx = 2;

// Adds a default `http` protocol to the url
export const prefixUrlWithProtocol = url => {
  const match = protocolRegex.exec(url);
  if (!match) {
    throw new Error(`Invalid URL ${url}. Expected valid URL with a protocol.`);
  }
  const protocol = match[protocolIdx] || 'http:';
  return `${protocol}//${match[urlIdx]}`;
};